import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";

const reviews = [
  {
    id: 1,
    name: "Sarah M.",
    location: "Austin, TX",
    rating: 5,
    text: "Arrived in 2 days, works perfectly. We lost power for 3 days last spring — this thing kept our fridge, lights, and router running the whole time.",
    avatar: "https://public.readdy.ai/ai/img_res/fe6b318b24817816c683482439743a46.jpg",
  },
  {
    id: 2,
    name: "James T.",
    location: "Nashville, TN",
    rating: 5,
    text: "Compared 4 other stores before buying here. Iron Haven beat everyone on price AND had the fastest shipping. The warranty support team is legit too.",
    avatar: "https://readdy.ai/api/search-image?query=friendly%20smiling%20man%20in%20his%2040s%20portrait%20professional%20headshot%20natural%20lighting%20clean%20white%20background%20realistic%20photo&width=80&height=80&seq=rev002&orientation=squarish",
  },
  {
    id: 3,
    name: "Laura K.",
    location: "Charlotte, NC",
    rating: 5,
    text: "I was nervous buying something this expensive online. Marcus walked me through the whole setup by phone. You just don't get that service anywhere else.",
    avatar: "https://readdy.ai/api/search-image?query=smiling%20woman%20in%20her%2030s%20portrait%20professional%20headshot%20warm%20lighting%20clean%20light%20background%20realistic%20photo&width=80&height=80&seq=rev003&orientation=squarish",
  },
];

const platforms = [
  { icon: "ri-google-line", name: "Google", score: "4.9", count: "643" },
  { icon: "ri-star-line", name: "Trustpilot", score: "4.8", count: "389" },
  { icon: "ri-shield-check-line", name: "Verified Buyers", score: "4.7", count: "215" },
];

function StarRow({ rating, size = "text-sm" }: { rating: number; size?: string }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`${s <= rating ? "ri-star-fill text-amber-400" : "ri-star-line text-amber-400/30"} ${size}`}
        ></i>
      ))}
    </div>
  );
}

export default function ReviewsAggregate() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="bg-zinc-900 border-b border-zinc-800 py-10 md:py-14 overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Top row: aggregate score + platform badges */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-12 mb-10">
          {/* Big aggregate score */}
          <div
            className={`flex-shrink-0 flex flex-col items-center md:items-start gap-2 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          >
            <div className="flex items-end gap-2">
              <span className="text-6xl font-black text-white leading-none">4.8</span>
              <div className="pb-2">
                <StarRow rating={5} size="text-xl" />
                <p className="text-gray-400 text-xs mt-1 whitespace-nowrap">out of 5 stars</p>
              </div>
            </div>
            <p className="text-amber-400 font-semibold text-sm">1,247 verified customer reviews</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex -space-x-2">
                {reviews.map((r) => (
                  <img
                    key={r.id}
                    src={r.avatar}
                    alt={r.name}
                    className="w-7 h-7 rounded-full border-2 border-zinc-900 object-cover"
                  />
                ))}
                <div className="w-7 h-7 rounded-full border-2 border-zinc-900 bg-amber-500 flex items-center justify-center text-white text-[9px] font-bold">
                  +1k
                </div>
              </div>
              <span className="text-gray-500 text-xs">Real homeowners, real results</span>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px self-stretch bg-zinc-700/60"></div>

          {/* Platform breakdown */}
          <div
            className={`flex flex-col gap-3 flex-shrink-0 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          >
            <p className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-1">
              Across All Platforms
            </p>
            {platforms.map((p) => (
              <div key={p.name} className="flex items-center gap-3">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className={`${p.icon} text-amber-400 text-base`}></i>
                </div>
                <span className="text-gray-300 text-sm w-28 whitespace-nowrap">{p.name}</span>
                <StarRow rating={5} size="text-xs" />
                <span className="text-white font-bold text-sm ml-1">{p.score}</span>
                <span className="text-gray-500 text-xs">({p.count})</span>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px self-stretch bg-zinc-700/60"></div>

          {/* Rating distribution bar */}
          <div
            className={`flex flex-col gap-2 flex-1 min-w-0 max-w-xs transition-all duration-700 delay-150 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          >
            <p className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-1">
              Rating Breakdown
            </p>
            {[
              { stars: 5, pct: 82 },
              { stars: 4, pct: 13 },
              { stars: 3, pct: 3 },
              { stars: 2, pct: 1 },
              { stars: 1, pct: 1 },
            ].map((row) => (
              <div key={row.stars} className="flex items-center gap-2">
                <span className="text-gray-400 text-xs w-8 flex-shrink-0 text-right">{row.stars}★</span>
                <div className="flex-1 bg-zinc-700 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all duration-1000"
                    style={{ width: visible ? `${row.pct}%` : "0%" }}
                  ></div>
                </div>
                <span className="text-gray-500 text-xs w-7 flex-shrink-0">{row.pct}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Review cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {reviews.map((r, i) => (
            <div
              key={r.id}
              className={`bg-zinc-800 rounded-lg p-5 border border-zinc-700/50 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${200 + i * 80}ms` }}
            >
              <div className="flex items-start gap-3 mb-3">
                <img
                  src={r.avatar}
                  alt={r.name}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-amber-500/30"
                />
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">{r.name}</p>
                  <p className="text-gray-500 text-xs">{r.location}</p>
                </div>
                <div className="ml-auto flex-shrink-0">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-double-quotes-l text-amber-500/40 text-2xl leading-none"></i>
                  </div>
                </div>
              </div>
              <StarRow rating={r.rating} size="text-xs" />
              <p className="text-gray-300 text-sm leading-relaxed mt-2 line-clamp-3">{r.text}</p>
              <div className="mt-3 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-verified-badge-fill text-amber-400 text-xs"></i>
                </div>
                <span className="text-gray-500 text-xs">Verified Purchase</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-zinc-800">
          <p className="text-gray-500 text-sm">
            <span className="text-amber-400 font-semibold">97% of customers</span> say they&apos;d recommend Iron Haven to a friend or family member
          </p>
          <Link
            to="/faq"
            className="flex items-center gap-2 text-white border border-zinc-600 hover:border-amber-500 hover:text-amber-400 px-5 py-2 rounded-md text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-down-s-line"></i>
            </div>
            Read More Reviews
          </Link>
        </div>
      </div>
    </section>
  );
}
